// Route-guard helper for the TanStack Router `beforeLoad` hook.
//
// Auth is resolved once on app boot via `checkAuthStatus()` and the result is
// cached in a module-level promise. Subsequent navigations gate on the presence
// of the local token (`getToken()`) so they stay synchronous and cheap. A guarded
// route that finds no session redirects to `/login?redirect=<intended path>`.
// Vendored from words-interface's lib/auth-guard.ts.

import { checkAuthStatus, getToken } from '@/lib/auth-service';

let bootStatusPromise: Promise<boolean> | null = null;

/**
 * Resolve (and cache) the boot-time auth status. Called once; later callers reuse
 * the same in-flight / settled promise. Reset via {@link resetAuthGuardCache}.
 */
function resolveBootStatus(): Promise<boolean> {
  if (bootStatusPromise === null) {
    bootStatusPromise = checkAuthStatus()
      .then((status) => status.authenticated)
      .catch(() => false);
  }
  return bootStatusPromise;
}

/** Clear the cached boot status (used after logout and in tests). */
export function resetAuthGuardCache(): void {
  bootStatusPromise = null;
}

/**
 * Returns `true` when the current user may access guarded routes.
 *
 * If a token is already present we trust it synchronously (fast path for
 * post-login navigations). Otherwise we await the cached boot status check.
 */
export async function isAuthenticated(): Promise<boolean> {
  if (getToken() !== null) {
    return true;
  }
  return resolveBootStatus();
}
